import * as Api from "@api";
import Link from "next/link";
import React from "react";
import { MainMenuProductList, MainMenuProductListItems } from "./MainMenuProductList";
import { MainMenuSection } from "./MainMenuSection";
import css from "./main-menu.module.scss";
import VadImage from "vadimages-nextjs-image-optimizer";
import { ObfuscatedEmail } from "../ObfuscatedEmail";

interface MainMenuProps
{
    products: Api.ProductFull[];
}

const productSections: MainMenuProductListItems[] = [
    {
        key: "singular-pod-platform",
        slugs: ["axil-s1-battery", "offset-s1-battery", "air-pod-postless", "pro-pod-ceramic", "classic-pod-stainless", "singular-510-adapter"],
        title: "Singular Vape Platform",
        link: "/singular",
    },
    {
        key: "all-in-ones",
        slugs: ["active-medley", "active-axil-mini-pro", "active-axil-mini-air", "active-axil", "active-axil-edge", "active-axil-curve", "active-seedling", "active-leaf", "active-stem", "active-glass", "active-node", "active-ceramic"],
        title: "All-In-Ones",
        link: "/products#all-in-ones",
    },
    {
        key: "ez-click-cartridges",
        slugs: ["ezclick-poly", "ezclick-xl-poly", "ezclick-glass", "active-ezclick-poly-plus", "active-ezclick-glass-plus", "active-ezclick-xl-glass-plus", "ezclick-ceramic-pro", "ezclick-xl-ceramic-pro"],
        title: "EZ Click Cartridges",
        link: "/products#cartridges",
    },
    {
        key: "batteries",
        slugs: ["active-sprout", "active-root-xl", "active-clone", "active-stik", "active-seed", "active-alpha", "active-root"],
        title: "Batteries",
        link: "/products#batteries",
    }
];

export const MainMenu = ({ products }: MainMenuProps): React.FunctionComponentElement<MainMenuProps> =>
    <div className={`${css["wrapper"]} ${css["menu-horizontal-scroll"]}`} data-logic-feature="main-menu">
        <div className={css["menu"]} data-main-menu >
            <button type="button" className={css["cross-button"]} close-button="true">
                <VadImage src={"/images/icons/cross.svg"} width={25} height={25} alt={''}/>
            </button>
            <div className={css["group"]} data-main-menu-group={"products"}>
                {
                    productSections.map(listItems => <MainMenuProductList key={listItems.key} listItems={listItems} products={products} />)
                }
            </div>
            <div className={css["group"]} data-main-menu-group={"customize"}>
                <MainMenuSection mobileHeight={280} title={"Customize"}>
                    <div className={css["customize"]}>
                        <Link href="/classic-customizations">
                            <figure>
                                <VadImage
                                    src="/images/@global/main-menu/classic-customizations.png"
                                    alt="Customization"
                                    width={250}
                                    height={164}
                                    mobileSrc="/images/@global/main-menu/classic-customizations.png"
                                    mobileWidth={140}
                                    mobileHeight={140}
                                />
                            </figure>
                            <div>
                                <h6>Customization</h6>
                                <p>Proven ACTIVE hardware, customized with your colors, graphics, and finishes</p>
                            </div>
                        </Link>
                        <Link href="/signature-design">
                            <figure>
                                <VadImage src="/images/@global/main-menu/exclusive-customizations.png" alt="Signature Design" width={250} height={164} />
                            </figure>
                            <div>
                                <h6>Signature Design</h6>
                                <p>Exclusive hardware, designed from the ground up around your brand</p>
                            </div>
                        </Link>
                    </div>
                </MainMenuSection>
            </div>
            <div className={css["group"]} data-main-menu-group={"why-active"}>
                <MainMenuSection mobileHeight={464} title={"Why ACTIVE"}>
                    <div className={css["link-content"]}>
                        <div className={css["link-block"]}>
                            <h6>About Us</h6>
                            <ul>
                                <li><Link href="/about-active-vapor-devices">Cannabis origins</Link></li>
                                <li><Link href="/award-winning-cannabis-vapes">Awards</Link></li>
                                <li><Link href="/vape-safety-and-quality-standards">Safety</Link></li>
                            </ul>
                        </div>
                        <div className={css["link-block"]}>
                            <h6>Our Technology</h6>
                            <ul>
                                <li><Link href="/no-burn-vapor-technology">No-Burn Technology®</Link></li>
                                <li><Link href="/technology/ac3-core">AC3™ Silicon Carbide Core</Link></li>
                                <li><Link href="/automated-filling-and-capping">Automation</Link></li>
                                <li><Link href="/hempzyme">HempZyme™</Link></li>
                            </ul>
                        </div>
                        <div className={css["link-block"]}>
                            <h6>Newsroom</h6>
                            <ul>
                                <li><Link href="/news">Read the Latest</Link></li>
                            </ul>
                        </div>
                    </div>
                </MainMenuSection>
            </div>
            <div className={css["group"]} data-main-menu-group={"support"}>
                <MainMenuSection mobileHeight={324} title={"Support"}>
                    <div className={css["link-content"]}>
                        <div className={css["link-block"]}>
                            <h6>Resources</h6>
                            <ul>
                                <li><Link href="/filling-directions">Filling & capping</Link></li>
                                <li><Link href="/battery-tips-and-tricks">Product Tips & Tricks</Link></li>
                                {/*<li><Link href="/calendar">Cannabis Calendar</Link></li>*/}
                                <li><Link href="/battery-support">Battery Support</Link></li>
                                <li><Link href="/active-vapor-faq">FAQ</Link></li>
                            </ul>
                        </div>
                        <div className={css["link-block"]}>
                            <h6>Need More Help?</h6>
                            <ul>
                                <li><ObfuscatedEmail email="support@active710.com" /></li>
                                <li><Link href="tel:415-320-6209">415-320-6209</Link></li>
                            </ul>
                        </div>
                    </div>
                </MainMenuSection>
            </div>
            <div className={css["group"]} data-main-menu-group={"contact"}>
                <MainMenuSection mobileHeight={424} title={"Contact"}>
                    <div className={css["link-content"]}>
                        <div className={css["link-block"]}>
                            <h6>Sales</h6>
                            <ul>
                                <li><ObfuscatedEmail email="sales@active710.com" /></li>
                                <li><Link href="tel:415-320-6209">415-320-6209</Link></li>
                            </ul>
                        </div>
                        <div className={css["link-block"]}>
                            <Link href={`/europe`} className="text-[#8a8a8a] hover:underline"><h6>EU Sales</h6></Link>
                            <ul>
                                <li><Link href="/europe">Europe</Link></li>
                                <li><ObfuscatedEmail email="ari@active710.com" /></li>
                            </ul>
                        </div>
                        <div className={css["link-block"]}>
                            <h6>UK Sales</h6>
                            <ul>
                                <li><ObfuscatedEmail email="hello@gsq-trading.com" /></li>
                            </ul>
                        </div>
                        <div className={css["link-block"]}>
                            <h6>Support</h6>
                            <ul>
                                <li><ObfuscatedEmail email="support@active710.com" /></li>
                                <li><Link href="tel:415-320-6209">415-320-6209</Link></li>
                            </ul>
                        </div>
                        <div className={css["link-block"]}>
                            <h6>Marketing</h6>
                            <ul>
                                <li><ObfuscatedEmail email="marketing@active710.com" /></li>
                            </ul>
                        </div>
                    </div>
                </MainMenuSection>
            </div>
        </div>
    </div>;
